import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import SearchIcon from '@mui/icons-material/Search';
import { crudClient, type CrudClient, type CrudDoc } from '../lib/api';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { type DeadlineRule, formatDeadlineRules } from '../lib/deadlineRules';
import DeadlineRulesInput from './DeadlineRulesInput';

export type FieldType =
  | 'text'
  | 'multiline'
  | 'number'
  | 'bool'
  | 'select'
  | 'string-array'
  | 'json'
  | 'deadline-rules';

export interface FieldConfig {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  helperText?: string;
  showInTable?: boolean;
  /** Custom width for the form input (1=full, 0.5=half). Defaults to full. */
  width?: 0.5 | 1;
}

export interface EntityCrudPageProps {
  title: string;
  resourcePath: string;
  fields: FieldConfig[];
  /** Optional override for which fields to show as table columns. */
  tableColumns?: string[];
  /** Default values for a new record. */
  newRecordDefaults?: Record<string, unknown>;
  /** How to derive a row's display label (used in confirm dialogs). */
  getRowLabel?: (row: CrudDoc) => string;
}

const EMPTY = '—';

function renderCell(value: unknown, field: FieldConfig): React.ReactNode {
  if (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  )
    return <span style={{ color: '#999' }}>{EMPTY}</span>;
  switch (field.type) {
    case 'bool':
      return value ? <CheckIcon fontSize="small" color="success" /> : <CloseIcon fontSize="small" color="disabled" />;
    case 'string-array':
      return Array.isArray(value) ? (value as string[]).join(', ') : String(value);
    case 'json':
      return <code style={{ fontSize: 11 }}>{truncate(JSON.stringify(value), 60)}</code>;
    case 'multiline':
      return truncate(String(value), 80);
    case 'deadline-rules':
      return truncate(formatDeadlineRules(value as DeadlineRule[]), 80);
    default:
      return String(value);
  }
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

function buildEmpty(fields: FieldConfig[], defaults: Record<string, unknown> = {}): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    if (f.key in defaults) {
      out[f.key] = defaults[f.key];
      continue;
    }
    switch (f.type) {
      case 'bool':
        out[f.key] = false;
        break;
      case 'number':
        out[f.key] = '';
        break;
      case 'string-array':
        out[f.key] = [];
        break;
      case 'deadline-rules':
        out[f.key] = [];
        break;
      case 'json':
        out[f.key] = null;
        break;
      default:
        out[f.key] = '';
    }
  }
  return out;
}

function coerceForSubmit(form: Record<string, unknown>, fields: FieldConfig[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const v = form[f.key];
    if (f.type === 'number') {
      if (v === '' || v === null || v === undefined) {
        if (f.required) out[f.key] = 0;
        continue;
      }
      const n = Number(v);
      if (!Number.isNaN(n)) out[f.key] = n;
    } else if (f.type === 'bool') {
      out[f.key] = Boolean(v);
    } else if (f.type === 'string-array') {
      out[f.key] = Array.isArray(v) ? v : [];
    } else if (f.type === 'deadline-rules') {
      out[f.key] = Array.isArray(v) ? v : [];
    } else if (f.type === 'json') {
      out[f.key] = v;
    } else {
      if (v === '' || v === null || v === undefined) {
        if (f.required) out[f.key] = '';
        continue;
      }
      out[f.key] = v;
    }
  }
  return out;
}

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: FieldConfig;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  if (field.type === 'bool') {
    return (
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(value)}
            onChange={(_, checked) => onChange(checked)}
          />
        }
        label={field.label}
      />
    );
  }
  if (field.type === 'select') {
    return (
      <TextField
        select
        label={field.label}
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.target.value)}
        required={field.required}
        helperText={field.helperText}
        fullWidth
        size="small"
      >
        {(field.options ?? []).map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }
  if (field.type === 'string-array') {
    const text = Array.isArray(value) ? (value as string[]).join(', ') : '';
    return (
      <TextField
        label={field.label}
        value={text}
        onChange={(e) =>
          onChange(
            e.target.value
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          )
        }
        helperText={field.helperText ?? 'Comma-separated values'}
        fullWidth
        size="small"
      />
    );
  }
  if (field.type === 'deadline-rules') {
    const rules = Array.isArray(value) ? (value as DeadlineRule[]) : [];
    return (
      <DeadlineRulesInput
        value={rules}
        onChange={(next) => onChange(next)}
        helperText={field.helperText}
      />
    );
  }
  if (field.type === 'json') {
    const text = value === null || value === undefined ? '' : JSON.stringify(value, null, 2);
    return (
      <TextField
        label={field.label}
        value={text}
        onChange={(e) => {
          const raw = e.target.value;
          if (!raw.trim()) {
            onChange(null);
            return;
          }
          try {
            onChange(JSON.parse(raw));
          } catch {
            // Keep raw string while user is typing; final coercion happens on submit
            onChange(raw);
          }
        }}
        helperText={field.helperText ?? 'Valid JSON'}
        fullWidth
        multiline
        minRows={4}
        size="small"
      />
    );
  }
  if (field.type === 'multiline') {
    return (
      <TextField
        label={field.label}
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.target.value)}
        required={field.required}
        helperText={field.helperText}
        fullWidth
        multiline
        minRows={3}
        size="small"
      />
    );
  }
  return (
    <TextField
      type={field.type === 'number' ? 'number' : 'text'}
      label={field.label}
      value={(value as string | number) ?? ''}
      onChange={(e) => onChange(e.target.value)}
      required={field.required}
      helperText={field.helperText}
      fullWidth
      size="small"
    />
  );
}

export default function EntityCrudPage({
  title,
  resourcePath,
  fields,
  tableColumns,
  newRecordDefaults,
  getRowLabel,
}: EntityCrudPageProps) {
  const { canWriteAssessments } = useUserPermissions();
  const canMutateReferenceData = canWriteAssessments;

  const client: CrudClient<CrudDoc> = useMemo(() => crudClient<CrudDoc>(resourcePath), [resourcePath]);

  const [rows, setRows] = useState<CrudDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const limit = 25;

  // Editor dialog
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'create' | 'edit'>('create');
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, unknown>>({});

  // Delete confirm
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Bulk import
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<string>('');

  const columns = useMemo(() => {
    if (tableColumns) return fields.filter((f) => tableColumns.includes(f.key));
    const explicit = fields.filter((f) => f.showInTable);
    if (explicit.length > 0) return explicit;
    return fields.slice(0, 6);
  }, [fields, tableColumns]);

  // Debounce the search input so we don't hammer the API on every keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(t);
  }, [search]);

  const load = (targetPage = page, q = debouncedSearch) => {
    setLoading(true);
    setError('');
    client
      .listPaginated({ page: targetPage, limit, q: q || undefined })
      .then((res) => {
        setRows(res.data);
        setPageCount(res.pagination.pages);
        setTotal(res.pagination.total);
      })
      .catch((e: Error) => setError(e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setPage(1);
    load(1, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourcePath, debouncedSearch]);

  useEffect(() => {
    load(page, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const openCreate = () => {
    setEditorMode('create');
    setEditId(null);
    setForm(buildEmpty(fields, newRecordDefaults));
    setEditorOpen(true);
  };

  const openEdit = (row: CrudDoc) => {
    setEditorMode('edit');
    setEditId(row._id);
    const next: Record<string, unknown> = {};
    for (const f of fields) next[f.key] = row[f.key];
    setForm(next);
    setEditorOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const body = coerceForSubmit(form, fields);
      if (editorMode === 'create') {
        await client.create(body);
      } else if (editId) {
        await client.update(editId, body);
      }
      setEditorOpen(false);
      load();
    } catch (e) {
      setError((e as Error).message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await client.remove(deleteId);
      load();
    } catch (e) {
      setError((e as Error).message || 'Delete failed');
    } finally {
      setDeleteId(null);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    setImportResult('');
    setError('');
    try {
      const parsed = JSON.parse(importText);
      const items = Array.isArray(parsed) ? parsed : parsed?.items;
      if (!Array.isArray(items)) {
        throw new Error('JSON must be an array of records, or { items: [...] }');
      }
      const result = await client.bulkUpsert(items);
      setImportResult(JSON.stringify(result, null, 2));
      load();
    } catch (e) {
      setError((e as Error).message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const deleteTarget = rows.find((r) => r._id === deleteId);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {total.toLocaleString()} record{total === 1 ? '' : 's'}
          </Typography>
        </Box>
        {canMutateReferenceData && (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button variant="outlined" startIcon={<DownloadIcon />} href={client.exportUrl()}>
              Export
            </Button>
            <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setImportOpen(true)}>
              Bulk import
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New
            </Button>
          </Stack>
        )}
      </Box>

      <TextField
        size="small"
        placeholder="Search…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} /> }}
        sx={{ mb: 2, width: { xs: '100%', sm: 360 } }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant="outlined">
          <TableContainer sx={{ maxHeight: 'calc(100vh - 280px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((c) => (
                    <TableCell key={c.key}>{c.label}</TableCell>
                  ))}
                  {canMutateReferenceData && (
                    <TableCell align="right" width={100}>
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + (canMutateReferenceData ? 1 : 0)}
                      align="center"
                    >
                      No records
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row._id} hover>
                      {columns.map((c) => (
                        <TableCell key={c.key}>{renderCell(row[c.key], c)}</TableCell>
                      ))}
                      {canMutateReferenceData && (
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => setDeleteId(row._id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {pageCount > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination count={pageCount} page={page} onChange={(_, p) => setPage(p)} color="primary" />
        </Box>
      )}

      {/* Editor dialog */}
      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editorMode === 'create' ? `New ${title.replace(/s$/, '')}` : `Edit ${title.replace(/s$/, '')}`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1} direction="row" flexWrap="wrap" useFlexGap>
            {fields.map((f) => {
              const halfWidth = f.width === 0.5;
              const flexBasis = halfWidth ? 'calc(50% - 8px)' : '100%';
              return (
                <Box key={f.key} sx={{ flexBasis, flexGrow: 1, minWidth: 240 }}>
                  {f.type === 'bool' ? (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(form[f.key])}
                          onChange={(_, checked) =>
                            setForm((p) => ({ ...p, [f.key]: checked }))
                          }
                        />
                      }
                      label={f.label}
                    />
                  ) : (
                    <FieldInput
                      field={f}
                      value={form[f.key]}
                      onChange={(v) => setForm((p) => ({ ...p, [f.key]: v }))}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk import dialog */}
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk import</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Paste a JSON array of records (or {'{"items": [...]}'}). Existing rows
            with matching natural keys will be upserted in place.
          </Typography>
          <TextField
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            multiline
            minRows={10}
            maxRows={20}
            fullWidth
            placeholder='[{"abbreviation": "CA", ...}, ...]'
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12.5 } }}
          />
          {importResult && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Box component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: 12 }}>
                {importResult}
              </Box>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setImportOpen(false); setImportResult(''); setImportText(''); }}>
            Close
          </Button>
          <Button variant="contained" onClick={handleImport} disabled={importing || !importText.trim()}>
            {importing ? 'Importing…' : 'Run import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm delete</DialogTitle>
        <DialogContent>
          <Typography>
            Delete{' '}
            <strong>
              {deleteTarget ? (getRowLabel ? getRowLabel(deleteTarget) : deleteTarget._id) : ''}
            </strong>
            ? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
